import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Home, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://i.ibb.co/7xkFLsM4/tempus-logo-white-blue.png";

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, cancelled, error
  const [paymentData, setPaymentData] = useState(null);

  const sessionId = searchParams.get('session_id');
  const loadId = searchParams.get('load_id');
  const isCancelled = window.location.pathname.includes('cancel');

  useEffect(() => {
    if (isCancelled) {
      setStatus('cancelled');
      return;
    }

    if (!sessionId) {
      setStatus('error');
      return;
    }

    // Check payment status
    const checkPayment = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
        const data = await response.json();
        
        setPaymentData(data);
        
        if (data.payment_status === 'paid' || data.status === 'paid') {
          setStatus('success');
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else {
          // Payment might still be processing
          setStatus('processing');
          // Check again in 3 seconds
          setTimeout(checkPayment, 3000);
        }
      } catch (err) {
        console.error('Error checking payment:', err);
        setStatus('error');
      }
    };

    checkPayment();
  }, [sessionId, isCancelled]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="inline-block">
            <img src={LOGO_URL} alt="Tempus Dispatch" className="h-8" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Loading State */}
          {status === 'loading' && (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-8 text-center">
              <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Verifying Payment...</h2>
              <p className="text-gray-400">Please wait while we confirm your payment.</p>
            </div>
          )}

          {/* Processing State */}
          {status === 'processing' && (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-8 text-center">
              <Loader2 className="w-16 h-16 text-yellow-500 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Processing Payment...</h2>
              <p className="text-gray-400">Your payment is being processed. This may take a moment.</p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="bg-green-500/10 backdrop-blur-sm rounded-2xl border border-green-500/30 p-8 text-center">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Payment Successful! 🎉</h2>
              <p className="text-gray-300 mb-6">
                Thank you for your payment. Your transaction has been completed successfully.
              </p>
              
              {paymentData && (
                <div className="bg-slate-900/50 rounded-xl p-4 mb-6 text-left">
                  <div className="flex justify-between py-2 border-b border-slate-700">
                    <span className="text-gray-400">Amount Paid</span>
                    <span className="text-white font-bold">${paymentData.amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-400">Load ID</span>
                    <span className="text-white font-mono">{loadId?.slice(0, 8).toUpperCase()}</span>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors"
                >
                  <Home className="w-5 h-5" />
                  Back to Home
                </Link>
                <Link
                  to="/post-load"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors"
                >
                  <FileText className="w-5 h-5" />
                  Post Another Load
                </Link>
              </div>
            </div>
          )}

          {/* Cancelled State */}
          {status === 'cancelled' && (
            <div className="bg-yellow-500/10 backdrop-blur-sm rounded-2xl border border-yellow-500/30 p-8 text-center">
              <div className="w-20 h-20 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Payment Cancelled</h2>
              <p className="text-gray-300 mb-6">
                Your payment was cancelled. No charges were made to your account.
              </p>
              
              <div className="flex flex-col gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
                >
                  <Home className="w-5 h-5" />
                  Back to Home
                </Link>
              </div>
            </div>
          )}

          {/* Error State */}
          {(status === 'error' || status === 'expired') && (
            <div className="bg-red-500/10 backdrop-blur-sm rounded-2xl border border-red-500/30 p-8 text-center">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">
                {status === 'expired' ? 'Session Expired' : 'Payment Error'}
              </h2>
              <p className="text-gray-300 mb-6">
                {status === 'expired' 
                  ? 'Your payment session has expired. Please try again.'
                  : 'There was an issue with your payment. Please try again or contact support.'}
              </p>
              
              <div className="flex flex-col gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
                >
                  <Home className="w-5 h-5" />
                  Back to Home
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-gray-500 text-sm">
        <p>© 2026 Tempus Dispatch LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}
